import * as React from 'react';
import invariant from 'invariant';
import CssBaseline from '@mui/material/CssBaseline';
import { Theme, PaletteMode, useMediaQuery } from '@mui/material';
import { ThemeProvider, useColorScheme } from '@mui/material/styles';
import InitColorSchemeScript from '@mui/material/InitColorSchemeScript';

import { PaletteModeContext } from '../shared/context';
import { useLocalStorageState } from '../useLocalStorageState';

import { AppTheme } from './AppProvider';

const COLOR_SCHEME_STORAGE_KEY = 'toolpad-color-scheme';
const MODE_STORAGE_KEY = 'toolpad-mode';

function usePreferredMode(window?: Window) {
  const prefersDarkMode = useMediaQuery(
    '(prefers-color-scheme: dark)',
    window && {
      matchMedia: window.matchMedia,
    }
  );
  return prefersDarkMode ? 'dark' : 'light';
}

type ThemeMode = 'system' | PaletteMode;

type CssVarsTheme = Theme & { vars: Record<string, string> };

function isCssVarsTheme(theme: AppTheme): theme is CssVarsTheme {
  return 'vars' in theme;
}

interface LegacyThemeProviderProps {
  theme: AppTheme;
  window?: Window;
  children: React.ReactNode;
}

/**
 * Compatibility layer for classic v5 themes. It will handle state management for the theme switcher.
 * In the v6 theme, this state management is handled by `useColorScheme`. But this hook will crash if
 * not run under context with a css vars theme.
 */
function LegacyThemeProvider(props: LegacyThemeProviderProps) {
  const { theme, children, window: appWindow } = props;
  invariant(
    !isCssVarsTheme(theme),
    'This provider only accepts legacy themes.'
  );

  const isDualTheme = 'light' in theme || 'dark' in theme;

  const preferredMode = usePreferredMode(appWindow);
  const [userMode, setUserMode] = useLocalStorageState<ThemeMode>(
    MODE_STORAGE_KEY,
    'system'
  );

  const paletteMode =
    !userMode || userMode === 'system' ? preferredMode : userMode;
  const dualAwareTheme = React.useMemo(
    () =>
      isDualTheme
        ? (theme[paletteMode === 'dark' ? 'dark' : 'light'] ??
          theme[paletteMode === 'dark' ? 'light' : 'dark'])
        : theme,
    [isDualTheme, paletteMode, theme]
  );

  // The v5 shim, based on local state
  const paletteModeContextValue = React.useMemo(
    () => ({
      paletteMode,
      isDualTheme,
      setPaletteMode: setUserMode,
    }),
    [isDualTheme, paletteMode, setUserMode]
  );

  return (
    <ThemeProvider theme={dualAwareTheme}>
      <PaletteModeContext.Provider value={paletteModeContextValue}>
        <CssBaseline enableColorScheme />
        {children}
      </PaletteModeContext.Provider>
    </ThemeProvider>
  );
}

interface CssVarsPaletteModeProviderProps {
  window?: Window;
  children: React.ReactNode;
}

function CssVarsPaletteModeProvider(props: CssVarsPaletteModeProviderProps) {
  const { children, window: appWindow } = props;

  const preferredMode = usePreferredMode(appWindow);
  const { mode, setMode, allColorSchemes } = useColorScheme();

  // The v6 API, based on `useColorScheme`
  const paletteModeContextValue = React.useMemo(() => {
    return {
      setPaletteMode: setMode,
      isDualTheme: allColorSchemes.length > 1,
      paletteMode: !mode || mode === 'system' ? preferredMode : mode,
    };
  }, [allColorSchemes, mode, preferredMode, setMode]);

  return (
    <PaletteModeContext.Provider value={paletteModeContextValue}>
      {children}
    </PaletteModeContext.Provider>
  );
}

interface CssVarsThemeProviderProps {
  theme: Theme;
  nonce?: string;
  window?: Window;
  children: React.ReactNode;
}

function CssVarsThemeProvider(props: CssVarsThemeProviderProps) {
  const { theme, nonce, children, window: appWindow } = props;
  invariant(
    isCssVarsTheme(theme),
    'This provider only accepts CSS vars themes.'
  );

  return (
    <ThemeProvider
      theme={theme}
      disableNestedContext
      modeStorageKey={MODE_STORAGE_KEY}
      documentNode={appWindow?.document}
      colorSchemeStorageKey={COLOR_SCHEME_STORAGE_KEY}
      colorSchemeNode={appWindow?.document.documentElement}
    >
      <InitColorSchemeScript
        nonce={nonce}
        modeStorageKey={MODE_STORAGE_KEY}
        attribute={theme.colorSchemeSelector}
        colorSchemeStorageKey={COLOR_SCHEME_STORAGE_KEY}
      />
      <CssVarsPaletteModeProvider window={appWindow}>
        <CssBaseline enableColorScheme />
        {children}
      </CssVarsPaletteModeProvider>
    </ThemeProvider>
  );
}

interface AppThemeProviderProps {
  nonce?: string;
  theme: AppTheme;
  window?: Window;
  children: React.ReactNode;
}

/**
 * @ignore - internal component.
 */
function AppThemeProvider(props: AppThemeProviderProps) {
  const { theme, children, ...rest } = props;

  const useCssVarsProvider = isCssVarsTheme(theme);

  return useCssVarsProvider ? (
    <CssVarsThemeProvider theme={theme} {...rest}>
      {children}
    </CssVarsThemeProvider>
  ) : (
    <LegacyThemeProvider theme={theme} {...rest}>
      {children}
    </LegacyThemeProvider>
  );
}

export { AppThemeProvider };
